import { FileTypes } from 'utils/constants';
import { EVIDENCE_STATUSES, isVideoFile, normalizeVideoEvidence } from 'utils/videoGrading';

export const EvidenceLocatorKinds = Object.freeze({
    TEXT: 'text',
    PAGE: 'page',
    SLIDE: 'slide',
    PARAGRAPHS: 'paragraphs',
});

export const normalizeEvidenceText = (value) =>
    `${value || ''}`
        .replace(/\s+/g, ' ')
        .replace(/[“”]/g, '"')
        .replace(/[‘’]/g, "'")
        .replace(/[–—]/g, '-')
        .trim()
        .toLowerCase();

export const getEvidenceLocationId = ({ sourceId, fileId, index }) => {
    const base = String(sourceId || fileId || 'unscoped');
    return base.endsWith(`:${index}`) ? base : `${base}:${index}`;
};

export const formatEvidenceHighlights = (items, { sourceId } = {}) =>
    (items || []).map((value, index) => ({
        id: getEvidenceLocationId({ sourceId, fileId: value?.file_id, index }),
        value,
    }));

export const getEvidenceFileType = (file) => {
    const type = file?.type;
    if (isVideoFile(file)) return FileTypes.VIDEO;
    if (
        [
            'text/csv',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ].includes(type)
    )
        return FileTypes.SHEET;
    if (
        [
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ].includes(type)
    )
        return FileTypes.PRESENTATION;
    if (type === 'application/pdf') return FileTypes.PDF;
    if (type === 'text/html') return FileTypes.RICH_TEXT;
    if (
        ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(type)
    )
        return FileTypes.DOCUMENT;
    return null;
};

const asText = (value) => (typeof value === 'string' ? value : '');
const PAGE_ANCHOR_PATTERN = /(?:\bpages?\b|\bp\.?)\s*[:#-]?\s*(\d+)(?:\s*-\s*\d+)?/i;
const getSourceAnchorLabel = (value) => {
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(getSourceAnchorLabel).filter(Boolean).join(', ');
    if (!value || typeof value !== 'object') return '';
    return (
        asText(value.label) ||
        asText(value.title) ||
        asText(value.value) ||
        asText(value.text) ||
        asText(value.location) ||
        asText(value.anchor)
    );
};
const getPageNumber = (value) => {
    const match = typeof value === 'string' ? value.trim().match(PAGE_ANCHOR_PATTERN) : null;
    return match ? Number(match[1]) : null;
};
const asNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const indexToLetters = (index) => {
    let value = '';
    let current = index + 1;
    while (current > 0) {
        const remainder = (current - 1) % 26;
        value = String.fromCharCode(65 + remainder) + value;
        current = Math.floor((current - 1) / 26);
    }
    return value;
};

const getCellLabel = (value) => {
    if (!value || typeof value !== 'object') return '';
    const row = asNumber(value.row);
    const col = asNumber(value.col);
    return row === null || col === null ? '' : `${indexToLetters(col)}${row + 1}`;
};

const getSpreadsheetRanges = (raw) => {
    if (Array.isArray(raw.cell_ranges)) return raw.cell_ranges.filter((range) => typeof range === 'string' && range);
    if (typeof raw.cell_range === 'string' && raw.cell_range) return [raw.cell_range];
    const cell = getCellLabel(raw.highlight);
    return cell ? [cell] : [];
};

const getParagraphNumbers = (raw) => {
    const values = Array.isArray(raw.paragraph_numbers)
        ? raw.paragraph_numbers
        : raw.paragraph_number == null
          ? []
          : [raw.paragraph_number];
    return values.filter((value) => Number.isInteger(value) && value > 0);
};

const getLocator = ({
    category,
    sourceAnchor,
    sourceAnchorCells,
    cellRanges,
    pageNumber,
    slideNumber,
    paragraphNumbers,
}) => {
    switch (category) {
        case FileTypes.VIDEO:
            return null;
        case FileTypes.SHEET:
            return sourceAnchorCells || cellRanges.length
                ? { kind: EvidenceLocatorKinds.TEXT, value: sourceAnchorCells || cellRanges.join(', ') }
                : null;
        case FileTypes.PRESENTATION:
            return sourceAnchor
                ? { kind: EvidenceLocatorKinds.TEXT, value: sourceAnchor }
                : slideNumber !== null
                  ? { kind: EvidenceLocatorKinds.SLIDE, value: slideNumber }
                  : null;
        case FileTypes.PDF:
        case FileTypes.DOCUMENT:
        case FileTypes.RICH_TEXT:
            return pageNumber !== null
                ? { kind: EvidenceLocatorKinds.PAGE, value: pageNumber }
                : sourceAnchor
                  ? { kind: EvidenceLocatorKinds.TEXT, value: sourceAnchor }
                  : paragraphNumbers.length
                    ? { kind: EvidenceLocatorKinds.PARAGRAPHS, value: paragraphNumbers }
                    : null;
        default:
            return sourceAnchor ? { kind: EvidenceLocatorKinds.TEXT, value: sourceAnchor } : null;
    }
};

export const normalizeEvidenceLocation = ({ location, file, category }) => {
    const raw = typeof location === 'string' ? { highlight: location } : location || {};
    const type = raw.location_type || category || getEvidenceFileType(file);
    const normalizedCategory =
        type === 'document'
            ? FileTypes.DOCUMENT
            : type === 'slide'
              ? FileTypes.PRESENTATION
              : type === 'spreadsheet'
                ? FileTypes.SHEET
                : type === 'video'
                  ? FileTypes.VIDEO
                  : type;
    const video = normalizedCategory === FileTypes.VIDEO ? normalizeVideoEvidence(raw, file) : null;
    const explicitStatus = EVIDENCE_STATUSES.includes(raw.status) ? raw.status : undefined;
    const status =
        normalizedCategory === FileTypes.VIDEO ? video?.status || explicitStatus || 'uncertain' : explicitStatus;
    const quote = video?.quote || asText(raw.quote) || (typeof raw.highlight === 'string' ? raw.highlight : '');
    const explanation = video?.explanation || asText(raw.explanation);
    const rawSourceAnchorValue = raw.source_anchor;
    const rawSourceAnchor = asText(rawSourceAnchorValue);
    const sourceAnchor = getSourceAnchorLabel(rawSourceAnchorValue);
    const pageNumber =
        normalizedCategory === FileTypes.PDF || normalizedCategory === FileTypes.DOCUMENT
            ? getPageNumber(rawSourceAnchor)
            : null;
    const paragraphNumbers = getParagraphNumbers(raw);
    const slideNumber = Number.isInteger(raw.slide_number)
        ? raw.slide_number
        : normalizedCategory === FileTypes.PRESENTATION && Number.isInteger(raw.highlight)
          ? raw.highlight + 1
          : null;
    const cellRanges = getSpreadsheetRanges(raw);
    const spreadsheetAnchor =
        normalizedCategory === FileTypes.SHEET && rawSourceAnchor.includes('!')
            ? rawSourceAnchor.split('!').map((part) => part.trim())
            : null;
    const sourceAnchorSheet = spreadsheetAnchor?.[0] || '';
    const sourceAnchorCells = spreadsheetAnchor?.slice(1).join('!') || '';
    const isCsv = file?.type === 'text/csv';
    const sheetName = (!isCsv && (asText(raw.sheet_name) || sourceAnchorSheet)) || '';
    const locator = getLocator({
        category: normalizedCategory,
        sourceAnchor,
        sourceAnchorCells,
        cellRanges,
        pageNumber,
        slideNumber,
        paragraphNumbers,
    });

    const fileId = file?.id ?? raw.file_id;
    return {
        fileId,
        fileName: file?.name,
        category: normalizedCategory,
        status,
        quote,
        explanation,
        sourceAnchor,
        pageNumber,
        locator,
        speakerLabel: video?.speakerLabel || asText(raw.speaker_label),
        timeStart: video?.start ?? asNumber(raw.start),
        timeEnd: video?.end ?? asNumber(raw.end),
        sheetName,
        cellRanges,
        paragraphNumbers,
        slideNumber,
        target: {
            category: normalizedCategory,
            fileId,
            quote,
            pageNumber,
            sourceAnchor,
            paragraphNumbers,
            slideNumber,
            sheetName,
            cellRanges,
            start: video?.start ?? asNumber(raw.start),
            end: video?.end ?? asNumber(raw.end),
        },
    };
};
